import React, { useEffect, useState } from "react";
import { Home } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { usePrintT, FORM_ROWS } from "@/lib/printI18n";
import { useInfo } from "@/lib/printInfo";
import { useCamp } from "@/context/SettingsContext";
import PrintLogo from "@/components/print/PrintLogo";

const ICON = (n) => `${process.env.PUBLIC_URL || ""}/print/${n}.png`;

function Line({ label, wide = false, value }) {
  return (
    <div className={`flex items-end gap-2 ${wide ? "col-span-2" : ""}`}>
      <span className="text-[10px] text-slate-500 whitespace-nowrap">{label}</span>
      <span className="flex-1 border-b border-slate-400 h-[18px] text-[11px] font-semibold text-slate-900 px-1 leading-[18px]">{value || ""}</span>
    </div>
  );
}
const fmt = (d) => (d ? new Date(d).toLocaleDateString("nl-BE") : "");

export default function RegistrationForm({ lang, data, withMapBreak = true, copyLabel }) {
  const t = usePrintT(lang);
  const b = data?.booker || {};
  const st = data?.stay || {};
  const { camp } = useCamp();
  // eigen tekst van de camping in die taal, anders in het Nederlands, anders de standaardtekst
  const info = useInfo(lang, camp.info_items?.[lang] || camp.info_items?.nl);
  const items = info.items;
  const emergency = (camp.emergency || [])
    .filter((r) => r.number)
    .map((r) => [r.label || info.contacts[r.key] || r.key, r.number]);
  const half = Math.ceil(emergency.length / 2);
  const [extra, setExtra] = useState([]);
  const [quote, setQuote] = useState(null);
  const [qrLink, setQrLink] = useState(null);
  useEffect(() => {
    api.get("/tariff-items").then((r) => setExtra((r.data || []).filter((i) => !i.builtin && i.active !== false))).catch(() => {});
  }, []);
  // QR-code naar de zelf-aanvulpagina, enkel zinvol zolang de boeking nog niet definitief is
  useEffect(() => {
    if (!data?.id || data?.definitive) { setQrLink(null); return; }
    api.get(`/reservations/${data.id}/complete-link`).then((r) => setQrLink(r.data?.url || null)).catch(() => setQrLink(null));
  }, [data?.id, data?.definitive]);
  useEffect(() => {
    if (!data?.id || !data?.departure) return;
    api.get(`/tariffs/quote?reservation_id=${data.id}&until=${data.departure}&lang=${lang}`)
      .then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [data?.id, data?.departure, lang]);
  const money = (v) => `€ ${(Number(v) || 0).toFixed(2).replace(".", ",")}`;
  // Toeristenbelasting valt buiten de btw
  const vatBase = quote ? quote.lines.filter((l) => l.kind !== "tax").reduce((s, l) => s + l.qty * l.unit_price, 0) : 0;
  const vat = (vatBase * (quote?.vat_rate || 0)) / 100;
  return (
    <section className={`print-form pt-0 flex flex-col ${data && !withMapBreak ? "" : "break-before-page"}`} data-testid="registration-form">
      <div className="flex flex-col flex-1 print:min-h-[283mm]" data-testid="fiche-sheet">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 border-b-2 border-primary pb-1.5">
        <div>
          <PrintLogo camp={camp} testid="print-registration-logo" />
          <h2 className="font-display font-extrabold text-lg text-slate-900 leading-none" data-testid={copyLabel ? "form-copy-label" : undefined}>{copyLabel || t("form_title")}</h2>
        </div>
        {data && (
          <div className="self-center text-center flex flex-col items-center gap-1" data-testid="fiche-title">
            {qrLink && (
              <div className="flex flex-col items-center" data-testid="registration-qr">
                <QRCodeSVG value={qrLink} size={92} level="M" />
                <span className="text-[8px] text-slate-500 leading-none mt-1">{t("scan_to_complete")}</span>
              </div>
            )}
            <p className="font-display font-extrabold text-base text-slate-900 leading-tight">{data.booker?.first_name} {data.booker?.last_name}</p>
          </div>
        )}
        <div className="text-right text-[11px] leading-snug text-slate-700">
          <p className="font-display font-extrabold text-base text-primary leading-tight">{camp.camp_name}</p>
          <p>{camp.street}</p>
          <p>{camp.postal_city}</p>
          <p>{t("phone")} {camp.phone}</p>
          {camp.gps && <p>GPS: {camp.gps}</p>}
          <p>{t("mail")}: {camp.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-[1fr_1.15fr] gap-3 mt-1.5 flex-1">
        {/* Left: pitch + booker */}
        <div className="space-y-1.5 flex flex-col">
          <p className="font-light italic text-sm text-slate-500 leading-none" data-testid="form-hint">{t("fill_hint")}</p>
          <div className="rounded-lg border-2 border-primary bg-white px-3 py-1">
            <p className="text-[10px] uppercase tracking-wider text-primary font-semibold">{t("chosen_pitch")}</p>
            <div className="flex h-8 items-center justify-center font-display font-extrabold text-3xl text-primary leading-none" data-testid="chosen-pitch-value">{data?.pitch_label || ""}</div>
          </div>
          <div className="rounded-lg border border-slate-300 p-2.5">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{t("booker")}</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <Line label={t("first_name")} wide value={b.first_name} />
              <Line label={t("last_name")} wide value={b.last_name} />
              <Line label={t("mail")} wide value={b.email} />
              <Line label={t("street")} wide value={b.street} />
              <Line label={t("house")} value={b.house_number} />
              <Line label={t("postal")} value={b.postal_code} />
              <Line label={t("city")} wide value={b.city} />
              <Line label={t("country")} wide value={b.country} />
              <Line label={t("passport")} wide value={b.passport_number} />
              <Line label={t("issue_country")} wide value={b.passport_country} />
              <Line label={t("issue_place")} wide value={b.passport_place} />
              <div className="col-span-2 flex items-end gap-2">
                <span className="text-[10px] text-slate-500 whitespace-nowrap">{t("card_number")} <span className="text-[8px] text-slate-400">(ASCI, Key Europe, ADAC)</span></span>
                <span className="flex-1 border-b border-slate-400 h-5 text-[11px] font-semibold px-1 leading-5">{b.card_number || ""}</span>
              </div>
              <Line label={t("valid_until")} wide value={fmt(b.card_valid_until)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-1 mt-auto">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-500">{t("date")}</span>
              <span className="rounded border border-slate-400 h-16" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[10px] text-slate-500">{t("signature")}</span>
              <span className="rounded border border-slate-400 h-16" />
            </div>
          </div>
        </div>

        {/* Right: items column */}
        <div className="space-y-1.5 flex flex-col">
          <p className="font-light italic text-sm text-slate-500 text-center leading-none" data-testid="stay-title">{t("your_stay")}</p>
          <div className="rounded-lg border border-slate-300 bg-white px-3 py-2 grid grid-cols-2 gap-4 text-[10px] font-semibold text-slate-700" data-testid="form-period-box">
            <span className="flex items-center gap-1">{t("from")}: <span className="flex-1 border-b border-slate-500 h-4 text-[11px] px-1 leading-4">{fmt(data?.arrival)}</span></span>
            <span className="flex items-center gap-1">{t("to")}: <span className="flex-1 border-b border-slate-500 h-4 text-[11px] px-1 leading-4">{fmt(data?.departure)}</span></span>
          </div>
          <div className="rounded-lg border border-slate-300 overflow-hidden flex-1 flex flex-col">
            <div className="grid grid-cols-[44px_1fr_60px_1fr] items-center bg-secondary px-2 py-1 text-[9px] font-semibold text-slate-700">
              <span /><span>{t("kind")}</span><span className="text-center">{t("qty")}</span><span className="text-center">{t("plate")}</span>
            </div>
            {FORM_ROWS.filter((r) => !r.power).map((r) => {
              const val = r.key === "adults" ? st.adults : r.key === "children" ? st.children : r.key === "dog" ? st.dogs
                : (st.items || []).includes(r.key) ? (r.plate ? (st.plates?.[r.key] ? "✓" : "✗") : "✓") : "";
              return (
                <div key={r.key} className="grid grid-cols-[44px_1fr_60px_1fr] items-center border-t border-slate-200 px-2 py-px flex-1" data-testid={`form-row-${r.key}`}>
                  <div className="h-6 w-8 grid place-items-center">
                    {r.icon ? <img src={ICON(r.icon)} alt={r.key} className="max-h-6 max-w-[32px] object-contain" /> : <Home className="h-5 w-5 text-slate-400" />}
                  </div>
                  <span className="text-[9px] leading-tight text-slate-600">{t(r.key)}</span>
                  <span className="mx-auto h-6 w-10 rounded border border-slate-400 grid place-items-center text-[11px] font-bold text-slate-900" data-testid={`current-${r.key}`}>{data ? val : ""}</span>
                  <span className="px-2">{r.plate && <span className="block border-b border-slate-400 h-4 text-[10px] font-semibold leading-4 px-1">{data ? (st.plates?.[r.key] || "") : ""}</span>}</span>
                </div>
              );
            })}
            {extra.map((i) => {
              const val = i.countable ? ((st.counts || {})[i.key] || "") : ((st.items || []).includes(i.key) ? "✓" : "");
              return (
                <div key={i.key} className="grid grid-cols-[44px_1fr_60px_1fr] items-center border-t border-slate-200 px-2 py-px flex-1" data-testid={`form-row-${i.key}`}>
                  <div className="h-6 w-8 grid place-items-center"><Home className="h-5 w-5 text-slate-400" /></div>
                  <span className="text-[9px] leading-tight text-slate-600">{i.label}</span>
                  <span className="mx-auto h-6 w-10 rounded border border-slate-400 grid place-items-center text-[11px] font-bold text-slate-900" data-testid={`current-${i.key}`}>{data ? val : ""}</span>
                  <span />
                </div>
              );
            })}
            <div className="grid grid-cols-[44px_1fr_60px_1fr] items-center border-t border-slate-300 bg-secondary/30 px-2 py-px flex-1" data-testid="form-row-power">
              <div className="h-6 w-8 grid place-items-center"><img src={ICON("amp6")} alt="A" className="max-h-6 object-contain" /></div>
              <span className="text-[9px] leading-tight text-slate-600">{t("power")} <span className="italic text-slate-400">— {t("power_hint")}</span></span>
              <span className="mx-auto flex gap-1">
                {["6", "10"].map((a) => (
                  <span key={a} className="flex flex-col items-center"><span className={`h-5 w-5 rounded border border-slate-400 grid place-items-center text-[11px] font-bold ${st.power === a ? "text-emerald-600" : ""}`}>{st.power === a ? "✓" : ""}</span><span className="text-[7px] text-slate-600 leading-none mt-[1px]">{a} Amp</span></span>
                ))}
              </span>
              <span />
            </div>
          </div>
        </div>
      </div>

      {/* General info */}
      <div className="mt-1 rounded-lg border border-slate-300 px-3 py-1 break-inside-avoid" data-testid="general-info">
        <p className="text-[10px] uppercase tracking-wider text-primary font-semibold mb-1">{t("info")}</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-0.5">
          {items.map(([title, body]) => (
            <div key={title} className="break-inside-avoid">
              <p className="text-[9.5px] font-bold text-slate-900 leading-tight">{title}</p>
              <p className="text-[8px] leading-tight text-slate-600">{body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-1 grid grid-cols-3 gap-2 break-inside-avoid" data-testid="emergency-box">
        <div className="rounded-lg bg-rose-600 text-white px-3 py-2 flex flex-col">
          <p className="text-[10px] uppercase tracking-wider font-semibold">{info.emergency}</p>
          <div className="flex items-center gap-3 flex-1">
            <span className="font-display font-extrabold text-3xl leading-none">112</span>
            <div className="text-[9px] leading-snug font-medium">
              {info.e112.map((l) => <p key={l}>{l}</p>)}
            </div>
          </div>
        </div>
        {[emergency.slice(0, half), emergency.slice(half)].filter((col) => col.length).map((col, i) => (
          <div key={i} className="rounded-lg border border-slate-800 px-3 py-2 text-slate-900">
            {col.map(([label, num]) => (
              <div key={label + num} className="flex items-center justify-between text-[10px] leading-snug">
                <span>{label}</span><span className="font-bold tabular-nums">{num}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      </div>

      {/* Prijsindicatie — op de achterzijde (recto-verso), eigen pagina */}
      {quote?.lines?.length > 0 && (
        <div className="break-before-page pt-2" data-testid="print-price-page">
          <div className="flex items-center justify-between gap-6 border-b-2 border-primary pb-2">
            <div>
              <PrintLogo camp={camp} testid="print-price-logo" />
              <h2 className="font-display font-extrabold text-lg text-slate-900 leading-none">{t("price_estimate")}</h2>
            </div>
            <div className="text-right text-[11px] leading-snug text-slate-700">
              <p className="font-display font-extrabold text-base text-slate-900 leading-tight">{data?.booker?.first_name} {data?.booker?.last_name}</p>
              <p>{t("chosen_pitch")}: <span className="font-bold">{data?.pitch_label}</span></p>
              <p>{fmt(data?.arrival)} – {fmt(data?.departure)}</p>
            </div>
          </div>
          <div className="mt-3 rounded-lg border border-primary/60 bg-secondary/30 px-4 py-3" data-testid="print-price-estimate">
            <div className="grid grid-cols-2 gap-x-8">
              <ul className="space-y-1">
                {quote.lines.map((l, i) => (
                  <li key={i} className="flex justify-between gap-3 text-[11px] text-slate-700 border-b border-slate-200 pb-0.5" data-testid={`print-price-line-${i}`}>
                    <span className="truncate">{l.description}</span>
                    <span className="tabular-nums font-semibold shrink-0">{money(l.qty * l.unit_price)}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[11px] text-slate-700 space-y-1 self-start">
                <div className="flex justify-between gap-3"><span>{t("total")}</span><span className="tabular-nums font-semibold">{money(quote.subtotal)}</span></div>
                <div className="flex justify-between gap-3"><span>{t("vat")} {quote.vat_rate}%</span><span className="tabular-nums">{money(vat)}</span></div>
                <div className="flex justify-between gap-3 border-t border-slate-300 pt-1 font-display font-extrabold text-[14px] text-primary">
                  <span>{t("total_incl")}</span><span className="tabular-nums" data-testid="print-price-total">{money(quote.subtotal + vat)}</span>
                </div>
              </div>
            </div>
            <p className="mt-2 text-[8.5px] italic leading-snug text-slate-500">{t("price_note")}</p>
          </div>
        </div>
      )}
    </section>
  );
}
